/**
 * Workshop PreviewCardFrame — rich preview card (hero image + caption bar).
 *
 * NOTE: No official OSS component exists for this rich preview layout, so this
 * is a recreation pinned to the reference screenshot: large hero image
 * (16:10-ish, rounded top) over a glass caption bar with bold title, muted
 * source line.
 *
 * Offline-safe: the hero <img> sits on a gradient fallback tile, so a failed
 * load still renders a composed card.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PreviewCardFrameProps {
  imageUrl?: string;
  title: string;
  source: string;
  openLabel?: string;
  status?: FrameStatus;
  onOpen?: (target: string) => void;
  className?: string;
  "data-testid"?: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const FALLBACK_IMAGE_URL = "https://picsum.photos/seed/aman-tokyo/640/400";

// ─── Main component ───────────────────────────────────────────────────────────

export function PreviewCardFrame({
  imageUrl = FALLBACK_IMAGE_URL,
  title,
  source,
  status = "done",
  className,
  "data-testid": testId = "preview-card-frame",
}: PreviewCardFrameProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = imageUrl && !imgFailed;

  return (
    <div
      className={cn(surfaceRoleClassName("frame"), "group relative flex flex-col overflow-hidden rounded-frame", className)}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex flex-1 flex-col">
        {status === "error" ? (
          <div className="flex items-center gap-2 px-3 py-4" data-testid={`${testId}-error`}>
            <Icons.CircleAlert className="h-3.5 w-3.5 shrink-0 text-status-error" aria-hidden="true" />
            <span className="text-canvas-base text-muted">Couldn&apos;t load preview. Try again.</span>
          </div>
        ) : status === "streaming" ? (
          <div className="flex items-center gap-2 px-3 py-4" data-testid={`${testId}-loading`}>
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Loading preview…</span>
          </div>
        ) : !title ? (
          <p className="px-3 py-4 text-center text-canvas-base italic text-muted" data-testid={`${testId}-empty`}>
            No preview available
          </p>
        ) : (
          <>
            <div
              className="relative aspect-[16/10] w-full overflow-hidden rounded-t-frame bg-elevated bg-gradient-to-br from-accent/25 via-elevated to-elevated"
              data-testid={`${testId}-hero`}
            >
              {showImage ? (
                <img
                  src={imageUrl}
                  alt={title}
                  loading="lazy"
                  onError={() => setImgFailed(true)}
                  className="h-full w-full object-cover"
                  data-testid={`${testId}-hero-image`}
                />
              ) : (
                <div
                  className="flex h-full w-full items-center justify-center"
                  data-testid={`${testId}-hero-fallback`}
                  aria-label={title}
                >
                  <Icons.Image className="h-8 w-8 text-muted" aria-hidden="true" />
                </div>
              )}
            </div>

            <div
              className="nodrag flex items-center gap-2 px-3 py-2.5"
              data-testid={`${testId}-caption`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-canvas-base font-semibold leading-snug text-primary" data-testid={`${testId}-title`}>
                  {title}
                </p>
                {source && (
                  <p className="mt-0.5 truncate text-canvas-xs text-muted" data-testid={`${testId}-source`}>
                    {source}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { PreviewCardFrameProps };
